import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Zap, Navigation, Loader2, List, Map as MapIcon, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TaskMap from "../components/TaskMap";

const RADII = [
  { id: null, label: "Tous" },
  { id: 5, label: "≤ 5 km" },
  { id: 15, label: "≤ 15 km" },
  { id: 25, label: "≤ 25 km" },
];
const MAX_RADIUS = 25;

export default function TaskFeed() {
  const { user } = useAuth();
  const profile = user?.helper_profile || {};
  const isHelperNotApproved = user?.role === "helper" && !profile.approved;
  const appStatus = profile.application_status || "not_submitted";

  const [tasks, setTasks] = useState([]);
  const [category, setCategory] = useState("");
  const [coords, setCoords] = useState(null);
  const [radius, setRadius] = useState(25);
  const [geoBusy, setGeoBusy] = useState(false);
  const [view, setView] = useState("list");

  const askLocation = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Position activée");
        setGeoBusy(false);
      },
      () => { toast.error("Position refusée"); setGeoBusy(false); },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const load = async () => {
    const params = new URLSearchParams({ status: "published" });
    if (category) params.set("category", category);
    if (coords) {
      params.set("lat", coords.lat);
      params.set("lng", coords.lng);
      // Always cap at MAX_RADIUS km for helpers
      params.set("radius_km", Math.min(radius ?? MAX_RADIUS, MAX_RADIUS));
    }
    const { data } = await api.get(`/tasks?${params.toString()}`);
    setTasks(data.filter((t) => !t.helper_id));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isHelperNotApproved) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category, coords, radius]);

  const appliedRadius = coords ? Math.min(radius ?? MAX_RADIUS, MAX_RADIUS) : null;

  // BLOCK helpers not approved — full screen takeover
  if (isHelperNotApproved) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="helper-not-approved">
        <div className="card-surface p-8 md:p-12 text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
            <Lock size={28} />
          </div>
          <h1 className="mt-5 text-3xl md:text-4xl font-heading font-black text-navy">
            Profil en attente d'approbation
          </h1>
          <p className="mt-3 text-[#475569] max-w-md mx-auto">
            {appStatus === "not_submitted" && "Tu dois d'abord remplir ta candidature avant de pouvoir voir et accepter les tâches."}
            {appStatus === "pending" && "Ta candidature est en cours d'examen par l'équipe Go Helper. Réponse sous 24 à 48 h. On te contactera pour une courte entrevue téléphonique."}
            {appStatus === "rejected" && "Ta candidature a été refusée. Tu peux la modifier et la soumettre à nouveau."}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/helper/apply" className="btn-primary" data-testid="go-apply">
              {appStatus === "not_submitted" ? "Remplir ma candidature" : "Voir ma candidature"} <ArrowRight size={16} />
            </Link>
            <Link to="/dashboard/helper" className="btn-outline" data-testid="back-dashboard">
              Retour au tableau
            </Link>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 text-xs text-[#475569]">
            <div className="bg-surface rounded-xl p-3"><div className="font-bold text-navy">1. Candidature</div>Remplir le formulaire complet</div>
            <div className="bg-surface rounded-xl p-3"><div className="font-bold text-navy">2. Photos</div>Pièce d'identité + profil</div>
            <div className="bg-surface rounded-xl p-3"><div className="font-bold text-navy">3. Entrevue</div>Court appel avec l'équipe</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-navy">Tâches disponibles</h1>
          <p className="text-[#475569] mt-1">
            Accepte une tâche près de chez toi {coords ? `· rayon max ${MAX_RADIUS} km` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 bg-surface rounded-full" data-testid="view-toggle">
          <button onClick={() => setView("list")} data-testid="view-list"
            className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1 ${view === "list" ? "bg-navy text-white" : "text-navy"}`}>
            <List size={14} /> Liste
          </button>
          <button onClick={() => setView("map")} data-testid="view-map"
            className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1 ${view === "map" ? "bg-navy text-white" : "text-navy"}`}>
            <MapIcon size={14} /> Carte
          </button>
        </div>
      </div>

      <div className="card-surface p-4 flex items-center gap-3 flex-wrap" data-testid="geo-bar">
        {!coords ? (
          <button onClick={askLocation} disabled={geoBusy} className="btn-primary py-2 px-4 text-sm" data-testid="enable-geo">
            {geoBusy ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
            {geoBusy ? "..." : "Activer ma position"}
          </button>
        ) : (
          <span className="chip-lime" data-testid="geo-active"><Navigation size={12} /> Position activée</span>
        )}
        <div className="flex gap-1 flex-wrap">
          {RADII.map((r) => (
            <button key={String(r.id)} onClick={() => setRadius(r.id ?? MAX_RADIUS)} disabled={!coords}
              className={`chip whitespace-nowrap ${(radius === (r.id ?? MAX_RADIUS)) ? "bg-navy text-white" : ""} disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`radius-${r.id ?? "all"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <button onClick={() => setCategory("")} data-testid="filter-all"
          className={`chip whitespace-nowrap ${!category ? "bg-navy text-white" : ""}`}>Toutes</button>
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`filter-${c.id}`}
            className={`chip whitespace-nowrap ${category === c.id ? "bg-navy text-white" : ""}`}>{c.label}</button>
        ))}
      </div>

      {view === "map" ? (
        <TaskMap center={coords} tasks={tasks} radiusKm={appliedRadius} />
      ) : tasks.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#475569]">
          Aucune tâche {coords ? `dans un rayon de ${appliedRadius} km` : "pour cette catégorie"}.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {tasks.map((t) => (
            <Link key={t.id} to={`/tasks/${t.id}`} className="card-interactive p-5 block" data-testid={`feed-task-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="chip">{CATEGORIES.find((c) => c.id === t.category)?.label || t.category}</span>
                {t.urgent && <span className="chip-lime"><Zap size={12} /> Urgent</span>}
              </div>
              <h3 className="font-heading font-black text-navy text-xl mt-3">{t.title}</h3>
              <p className="text-sm text-[#475569] line-clamp-2 mt-1">{t.description}</p>
              <div className="flex items-center gap-3 mt-3 text-sm text-[#475569] flex-wrap">
                <span className="flex items-center gap-1"><MapPin size={14} /> {t.area}</span>
                {t.distance_km != null && (
                  <span className="chip-lime" data-testid={`distance-${t.id}`}>
                    <Navigation size={12} /> {t.distance_km} km
                  </span>
                )}
                {t.pricing_mode === "bids" && <span className="chip-navy">Soumission</span>}
              </div>
              <div className="flex items-end justify-between mt-4 pt-4 border-t border-[#E2E8F0]">
                <div>
                  {t.pricing_mode === "bids" ? (
                    <>
                      <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Propose ton prix</div>
                      <div className="font-heading font-black text-lg text-navy">
                        {t.budget_hint ? `Budget ~ ${t.budget_hint.toFixed(2)} $` : "Tu fixes ton prix"}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Tu reçois</div>
                      <div className="font-heading font-black text-3xl text-navy">{t.helper_subtotal.toFixed(2)} $</div>
                    </>
                  )}
                </div>
                <span className="btn-primary py-2 px-4 text-sm">Voir</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
